import { Sparkles, Users, XCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { Task } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  DataTable,
  type DataTableColumn,
} from "@/components/ui/data-table";
import { TaskStatusBadge } from "@/features/tasks/components/TaskStatusBadge";
import { isTaskActive, useCancelTask } from "@/features/tasks/hooks/useTasks";
import { formatDateTime, formatDuration } from "@/lib/date";

interface Props {
  tasks: Task[];
  /** Oculta a coluna de alvo (historico de um schedule ja tem o alvo). */
  hideTargetColumn?: boolean;
}

export function TasksTable({ tasks, hideTargetColumn }: Props) {
  const { t } = useTranslation("tasks");
  const cancelTask = useCancelTask();
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  async function handleCancel(e: React.MouseEvent, task: Task) {
    e.stopPropagation();
    setCancellingId(task.id);
    try {
      await cancelTask.mutateAsync(task);
      toast.success(t("toasts.cancelRequested"));
    } catch (err) {
      toastApiError(err);
    } finally {
      setCancellingId(null);
    }
  }

  const columns: DataTableColumn<Task>[] = [
    {
      key: "title",
      header: t("table.title"),
      render: (task) => (
        <div className="flex max-w-[280px] flex-col">
          <span className="truncate font-medium">{task.title}</span>
          <span className="truncate text-xs text-ink-500">
            {task.prompt}
          </span>
        </div>
      ),
    },
    ...(hideTargetColumn
      ? []
      : [
          {
            key: "target",
            header: t("table.target"),
            render: (task: Task) => (
              <span className="inline-flex items-center gap-1.5">
                {task.agent_id ? (
                  <Sparkles className="h-3.5 w-3.5 text-brand-500" />
                ) : (
                  <Users className="h-3.5 w-3.5 text-brand-500" />
                )}
                {task.agent_name ??
                  task.team_session_name ??
                  t("table.unknownTarget")}
              </span>
            ),
          } satisfies DataTableColumn<Task>,
        ]),
    {
      key: "status",
      header: t("table.status"),
      render: (task) => <TaskStatusBadge status={task.status} />,
    },
    {
      key: "tokens",
      header: t("table.tokens"),
      render: (task) => (
        <span className="tabular-nums">
          {task.tokens_used.toLocaleString("pt-BR")}
          {task.max_tokens
            ? ` / ${task.max_tokens.toLocaleString("pt-BR")}`
            : ""}
        </span>
      ),
    },
    {
      key: "created",
      header: t("table.createdAt"),
      render: (task) => formatDateTime(task.created_at),
    },
    {
      key: "duration",
      header: t("table.duration"),
      render: (task) =>
        task.started_at
          ? formatDuration(task.started_at, task.finished_at)
          : "—",
    },
    {
      key: "actions",
      header: "",
      className: "w-12 text-right",
      render: (task) =>
        isTaskActive(task.status) ? (
          <Button
            size="icon"
            variant="ghost"
            title={t("actions.cancel")}
            aria-label={t("actions.cancel")}
            disabled={cancellingId === task.id}
            onClick={(e) => void handleCancel(e, task)}
          >
            <XCircle className="h-4 w-4 text-danger-500" />
          </Button>
        ) : null,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={tasks}
      getRowKey={(task) => task.id}
      getRowHref={(task) => `/tasks/${task.id}`}
    />
  );
}
